import { Button } from '@neo/primitives/button'
import { Typography } from '@neo/primitives/typography'
import { useCallback, useEffect, useRef } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'router-v6'
import { getDefaultRoute } from 'v2/configs/routes'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { useMobile } from 'v2/shared/hooks/use-mobile'
import { AuthLayout } from 'v2/shared/layouts/auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { AnalyticsEvents, consumeSignupCountry, identifyUser, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { AUTH_PRIMARY_BUTTON_CLASS } from './components/auth-styles'
import { useCompleteSignupMutation } from './queries'

export const CompleteSignup = () => {
  const processingRef = useRef(false)
  const { mutate: completeSignup, error: completeSignupError } = useCompleteSignupMutation()
  const { userAuthenticated } = useAuth()
  const isMobile = useMobile()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const couponUUID = searchParams.get('coupon_uuid') ?? undefined

  const runCompleteSignup = useCallback(
    (signupToken: string) => {
      completeSignup(
        { token: signupToken, email: email || '', coupon_uuid: couponUUID },
        {
          onSuccess: (response) => {
            logger.info('Complete signup successful, redirecting to 2FA setup')
            // Identify by email first so USER_SIGNED_UP attaches to the person
            // (this may be a different tab than /signup — the email link — so the
            // session here is otherwise anonymous). aom_uuid is aliased in.
            identifyUser({
              email: response.data.email,
              aomId: response.data.aom_uuid,
              name: response.data.name,
              orgName: response.data.organization_name,
              region: response.data.region,
            })
            track(AnalyticsEvents.USER_SIGNED_UP, {
              org_name: response.data.organization_name,
              region: response.data.region,
              email: response.data.email,
              country: consumeSignupCountry(),
            })
            navigate('/two-factor-auth', {
              state: {
                aomUUID: response.data.aom_uuid,
                orgID: response.data.org_id,
                accountID: response.data.account_id,
                email: response.data.email,
                isSignup: true,
                verificationPending: false,
                originalLoginResponse: null,
                region: response.data.region,
              },
            })
          },
          onError: (error) => {
            logger.error('Complete signup failed', error)
          },
        },
      )
    },
    [completeSignup, email, couponUUID, navigate],
  )

  useEffect(() => {
    if (processingRef.current) return

    if (token) {
      processingRef.current = true
      runCompleteSignup(token)
    }
  }, [runCompleteSignup, token])

  // Prevent accidental page close/refresh during signup. Detached once the request has
  // failed: the error branch below keeps this component mounted, so without the guard the
  // browser would still warn "your signup is in progress" for a signup that already stopped.
  useEffect(() => {
    if (completeSignupError) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      return 'Your signup is in progress. Leaving may cause it to fail.'
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [completeSignupError])

  if (!token) {
    return <Navigate to="/signup" />
  }

  if (userAuthenticated) {
    if (isMobile) {
      return (
        <AuthLayout pageTitle="Welcome" title="Welcome aboard!" description="">
          <p className="text-p-400 text-secondary">
            Thanks for signing up. Open this app on desktop to access all features.
          </p>
        </AuthLayout>
      )
    }
    return <Navigate to={getDefaultRoute()} />
  }

  // The verification link is single-use — the first attempt consumes the token in AMS, so a
  // second POST with it always comes back "expired or invalid" (0 of the failed signups in a
  // 7-day prod window ever recovered by re-submitting). There is nothing to retry with THIS
  // link, and no reason to send the user back to /signup either: the email address is already
  // taken, so the signup form just answers "an account with this domain already exists".
  //
  // Logging in is the recovery path when the backend finished provisioning despite an abandoned
  // request; enforce_2fa then walks the user into 2FA setup. When the account is still pending
  // activation (expired/lost link), the login page's "Resend email" link leads to
  // /resend-activation-email for a fresh link (SER-6348).
  if (completeSignupError) {
    return (
      <AuthLayout pageTitle="Complete Signup" title="" description="">
        <div className="flex flex-col items-center justify-center gap-4">
          <Typography variant="S/600" className="text-center text-primary">
            We couldn't finish setting up your account
          </Typography>
          <Typography variant="S/400" className="text-center text-secondary">
            {getErrorText(completeSignupError)}
          </Typography>
          <Button
            typography="action"
            size="xl"
            className={AUTH_PRIMARY_BUTTON_CLASS}
            onClick={() => navigate('/login')}
            data-testid="button-complete-signup-login"
          >
            Go to login
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout pageTitle="Complete Signup" title="" description="">
      <div className="flex flex-col items-center justify-center">
        <img
          src="/Buddy_flying.webp"
          alt=""
          aria-hidden="true"
          className="h-28 w-auto pointer-events-none select-none"
        />
        <Typography variant="S/400" className="text-center text-secondary">
          Setting up your account...
        </Typography>
        <Typography variant="S/400" className="text-center text-tertiary z-10">
          Please do not refresh or close this tab. It may interrupt your signup process.
        </Typography>
      </div>
    </AuthLayout>
  )
}
